/**
 * Connector type slug helpers — slug ↔ API type ↔ display name.
 *
 * Used by the connector create form, the OAuth callback landing route,
 * and any other place that needs to render a slug as a human-readable
 * connector name.
 */

const SLUG_TO_API_TYPE: Record<string, string> = {
  sqlserver: "SqlServer",
  mysql: "MySql",
  cin7: "Cin7",
  xero: "Xero",
  vend: "Vend",
  ftp: "FTP",
  excel: "Excel",
  odata: "OData",
  uniconta: "Uniconta",
  gaap: "GAAP",
  gaapunity: "GAAPUnity",
  poweroffice: "PowerOffice",
  mailchimp: "Mailchimp",
  woocommerce: "WooCommerce",
  ceta: "Ceta",
  webhook: "Webhook",
  treasuryone: "TreasuryOne",
  simplepay: "SimplePay",
  harvest: "Harvest",
  zohocrm: "ZohoCRM",
  // Zoho PROJECTS — a different product and a different API from ZohoCRM above.
  // No DISPLAY_NAME_OVERRIDES entry needed: the capital-splitting heuristic turns
  // "ZohoProjects" into "Zoho Projects", which is what we want. ZohoCRM relies on
  // the same heuristic to become "Zoho CRM".
  zohoprojects: "ZohoProjects",
  payspacedeel: "PayspaceDeelPayroll",
  payspaceeelpayroll: "PayspaceDeelPayroll",
  wherehouse: "Wherehouse",
  medicaldirectorhelix: "MedicalDirectorHelix",
  bitrix24: "Bitrix24",
  asana: "Asana",
  salesforce: "SalesForce",
  hubspot: "HubSpot",
  // Oracle NetSuite. Needs a DISPLAY_NAME_OVERRIDES entry too — the heuristic
  // below would split "NetSuite" into "Net Suite", the same way it splits
  // "HubSpot".
  netsuite: "NetSuite",
};

/**
 * Display-name overrides for slugs whose canonical API type doesn't render
 * cleanly through the capital-splitting heuristic below (e.g. "SalesForce"
 * would otherwise become "Sales Force").
 */
const DISPLAY_NAME_OVERRIDES: Record<string, string> = {
  salesforce: "Salesforce",
  // Without this the heuristic below splits "HubSpot" into "Hub Spot".
  hubspot: "HubSpot",
  // Same: without this "NetSuite" renders as "Net Suite".
  netsuite: "NetSuite",
};

/**
 * Normalise a URL slug to match the B2C connector Type field.
 * e.g. "sqlserver" → "SqlServer", "cin7" → "Cin7", "poweroffice" → "PowerOffice".
 * Falls back to the original slug if unmapped.
 */
export function slugToApiType(slug: string): string {
  return SLUG_TO_API_TYPE[slug.toLowerCase()] ?? slug;
}

/**
 * Render a slug as a human-readable display name.
 * e.g. "poweroffice" → "Power Office", "xero" → "Xero".
 */
export function formatTypeName(slug: string): string {
  const override = DISPLAY_NAME_OVERRIDES[slug.toLowerCase()];
  if (override) return override;
  const apiType = slugToApiType(slug);
  // Insert a space before each capital that follows a lowercase letter.
  return apiType.replace(/([a-z])([A-Z])/g, "$1 $2");
}
